/**
 * PMS.V2.8 · RBAC V2 · Phase 2 — Roles & Permissions tree model.
 *
 * SINGLE STRUCTURAL AUTHORITY: the Roles & Permissions hierarchy is built
 * by CONSUMING the exact Sidebar navigation definition (TOP_LEVEL + GROUPS
 * exported from components/Layout.jsx) — never a second hand-maintained
 * tree. Same hierarchy, same parent/child, same sequence, same labels.
 *
 * CANONICAL IDENTITY: a function's permission module code is resolved from
 * its EXPLICIT `perm` reference first, then by matching its route against
 * the Phase-1 canonical registry (`/rbac/modules[].route`). We NEVER derive
 * a key from the slug / label / navSlug() here. A node whose canonical
 * module is not registered is surfaced as `unmapped` (registry alignment is
 * a later authorized phase) rather than silently minting a derived code.
 */
import { TOP_LEVEL, GROUPS } from "@/components/Layout";

// Ordered action vocabulary (matches the backend canonical registry).
export const ORDERED_ACTIONS = [
  "view", "create", "edit", "delete", "approve", "unapprove",
  "post", "release", "reverse", "cancel", "void", "reopen",
  "submit", "pass", "hold", "reject", "print", "export",
];
export const CORE_ACTIONS = ["view", "create", "edit", "delete", "approve", "print", "export"];
export const ACTION_LABEL = {
  view: "View", create: "Create", edit: "Edit", delete: "Delete", approve: "Approve",
  unapprove: "Unapprove", post: "Post", release: "Release", reverse: "Reverse",
  cancel: "Cancel", void: "Void", reopen: "Reopen", submit: "Submit", pass: "Pass",
  hold: "Hold", reject: "Reject", print: "Print", export: "Export",
};

function stripQuery(to) { return String(to || "").split("?")[0]; }

// ── Phase 3B Stage-1 · EXPLICIT SIDEBAR → GRANULAR CANONICAL MAP ──────────
// Owner-approved (Phase 3A.1) granular split. Each over-broad SHARED code
// (production_department_entry ×17, production_orders→Pending WO,
// masters_number_series→Accounting Year, reports→Permission Reference) is
// repointed to its OWN per-function canonical identity so a grant on one
// department / report never implies the other 16. These keys are registered
// in the backend (rbac.py · _GRANULAR_SPLIT_ROWS) so byCode resolves them.
//
// SCOPE: this map drives the Roles & Permissions UI ONLY. Live Sidebar /
// RouteGuard still resolve these routes via Layout.jsx `perm` / App.js guards
// exactly as before — their alignment is the later Owner-authorized Phase 3B
// migration. No live access changes here. `actions` is the per-function
// applicable subset (intersected with the module's real action set at resolve
// time): reconciliations / registers expose View / Print / Export only while
// entries expose write actions. VALID-SHARED codes (stock_engine 4 tabs,
// holidays) are intentionally left shared.
const _ENTRY_ACTIONS = ["view", "create", "edit", "post", "reverse", "print", "export"];
const _REPORT_ACTIONS = ["view", "print", "export"];
export const SIDEBAR_PERM_MAP = {
  // Production execution — GRANULAR per-department entry keys
  "/production/warping-entry": { code: "warping_entry", actions: _ENTRY_ACTIONS },
  "/production/covering-spandex-entry": { code: "covering_entry", actions: _ENTRY_ACTIONS },
  "/production/weaving-consumption": { code: "weaving_consumption", actions: _ENTRY_ACTIONS },
  "/production/finishing-entry": { code: "finishing_entry", actions: _ENTRY_ACTIONS },
  "/production/dyeing-entry": { code: "dyeing_entry", actions: _ENTRY_ACTIONS },
  "/production/checking-entry": { code: "checking_entry", actions: _ENTRY_ACTIONS },
  // Production registers — GRANULAR (view/print/export only)
  "/reports/warping-production-register": { code: "warping_register", actions: _REPORT_ACTIONS },
  "/reports/covering-production-register": { code: "covering_register", actions: _REPORT_ACTIONS },
  "/reports/spandex-warping-production-register": { code: "spandex_register", actions: _REPORT_ACTIONS },
  "/reports/checking-production-register": { code: "checking_register", actions: _REPORT_ACTIONS },
  // Production reconciliations — GRANULAR (view/print/export only)
  "/reports/warping-reconciliation": { code: "warping_reconciliation", actions: _REPORT_ACTIONS },
  "/reports/covering-production-reconciliation": { code: "covering_prod_reconciliation", actions: _REPORT_ACTIONS },
  "/reports/covering-rm-reconciliation": { code: "covering_rm_reconciliation", actions: _REPORT_ACTIONS },
  "/reports/weaving-production-reconciliation": { code: "weaving_prod_reconciliation", actions: _REPORT_ACTIONS },
  "/reports/weaving-rm-reconciliation": { code: "weaving_rm_reconciliation", actions: _REPORT_ACTIONS },
  "/reports/finishing-production-reconciliation": { code: "finishing_prod_reconciliation", actions: _REPORT_ACTIONS },
  "/reports/dyeing-production-reconciliation": { code: "dyeing_prod_reconciliation", actions: _REPORT_ACTIONS },
  // Pending Work Order — SPLIT from production_orders (Work Orders keeps production_orders)
  "/reports/pending-work-order": { code: "reports_pending_work_order", actions: _REPORT_ACTIONS },
  // Permission Reference — SPLIT from reports (Reports & Analytics keeps reports)
  "/reports/permission-reference": { code: "reports_permission_reference", actions: _REPORT_ACTIONS },
  // Accounting Year — SPLIT from masters_number_series (Number Series keeps masters_number_series)
  "/masters/accounting-year": { code: "masters_accounting_year", actions: ["view", "create", "edit"] },
  // ── VALID SHARED (kept per Phase 3A.1) ──
  // Stock Engine (V2) tabs — one screen, 4 tabs → one canonical boundary
  "/inventory/v2?tab=book": { code: "stock_engine", actions: _REPORT_ACTIONS },
  "/inventory/v2?tab=register": { code: "stock_engine", actions: _REPORT_ACTIONS },
  "/inventory/v2?tab=ledger": { code: "stock_engine", actions: _REPORT_ACTIONS },
  "/inventory/v2?tab=reconcile": { code: "stock_engine", actions: ["view", "print", "export", "reverse"] },
  // FG warehouse receipt — NEW canonical (Phase 2A)
  "/inventory/fg-warehouse-receipt": { code: "fg_warehouse_receipt", actions: ["view", "create", "edit", "post", "reverse", "print", "export"] },
  // Holiday Calendar — SHARED CANONICAL: holidays (backend gates holidays:*)
  "/settings/holiday-calendar": { code: "holidays", actions: ["view", "create", "edit", "delete"] },
};

// Codes referenced by MORE THAN ONE override route are genuinely shared; a
// single-mapped override is a granular (unique) identity and must NOT show
// the "shared" badge (Phase 3B granular splits collapse the former shared
// production/accounting/pending/permission-reference badges to none).
const _OVERRIDE_CODE_FREQ = (() => {
  const f = {};
  Object.values(SIDEBAR_PERM_MAP).forEach(v => { f[v.code] = (f[v.code] || 0) + 1; });
  return f;
})();

// Resolve one sidebar node → a function descriptor with its canonical module.
function resolveFunction(node, byCode, byRoute) {
  const override = SIDEBAR_PERM_MAP[node.to] || SIDEBAR_PERM_MAP[stripQuery(node.to)];
  const explicit = node.perm ? String(node.perm).split(":")[0] : null;
  let code = null, module = null, actionsOverride = null, shared = false;
  if (override) { code = override.code; module = byCode[code]; actionsOverride = override.actions || null; shared = (_OVERRIDE_CODE_FREQ[code] || 0) > 1; }
  if (!module) {
    code = explicit;
    module = code ? byCode[code] : null;
    if (!module) {
      // EXACT route match against the canonical registry (not a slug).
      const m = byRoute[node.to] || byRoute[stripQuery(node.to)];
      if (m) { code = m.code; module = m; }
    }
  }
  let actions = module ? module.actions : [];
  if (module && actionsOverride) actions = actionsOverride.filter(a => module.actions.includes(a));
  return {
    id: node.testId || node.to,
    label: node.label,
    route: node.to,
    adminOnly: !!node.adminOnly,
    code: code || null,
    actions,
    unmapped: !module,
    shared,
    intendedCode: explicit || null,     // referenced but unregistered, when unmapped
    legacy: !!(module && (module.legacy || module.deprecated)),
    canonical: !!(module && module.canonical),
    structuralCleanup: !!(module && module.structural_cleanup_required),
  };
}

/**
 * Build the sidebar-mirrored tree.
 * Shape: [{ key, label, subgroups:[{ key, label, functions:[fn] }] }]
 * Flat groups are normalised to a single nameless subgroup so the renderer
 * is uniform. TOP_LEVEL becomes the first "Top Level" branch.
 */
// A1-D5 · read-side-only aliases (backend `_NON_ASSIGNABLE_ALIASES`) are never offered for assignment.
export const NON_ASSIGNABLE_ALIASES = new Set(["management_approvals"]);
const assignable = fn => !NON_ASSIGNABLE_ALIASES.has(fn.code);

export function buildRbacTree(modules) {
  const byCode = {}; const byRoute = {};
  (modules || []).forEach(m => { byCode[m.code] = m; if (m.route) byRoute[m.route] = m; });

  const sections = [];

  sections.push({
    key: "top-level",
    label: "Top Level",
    subgroups: [{
      key: "top-level-fns",
      label: null,
      functions: TOP_LEVEL.map(n => resolveFunction(n, byCode, byRoute)).filter(assignable),
    }],
  });

  GROUPS.forEach(g => {
    let subgroups;
    if (Array.isArray(g.subGroups)) {
      subgroups = g.subGroups.map(sg => ({
        key: sg.key,
        label: sg.label,
        functions: (sg.items || []).map(n => resolveFunction(n, byCode, byRoute)).filter(assignable),
      }));
    } else {
      subgroups = [{
        key: `${g.key}-flat`,
        label: null,
        functions: (g.items || []).map(n => resolveFunction(n, byCode, byRoute)).filter(assignable),
      }];
    }
    sections.push({ key: g.key, label: g.label, subgroups });
  });

  attachAdditionalPermissions(sections, modules);
  return sections;
}

// P2-F3B · Additional Permissions — registered NON-navigational modules (no route) that no sidebar node
// represents BY CODE, excluding legacy / deprecated / superseded / retirement rows. Placed under the section
// whose label equals the module's registry group, else a standalone "Additional Permissions" branch.
const _RETIRED_FLAGS = ["legacy", "deprecated", "superseded", "retirement_candidate"];
export function additionalModules(modules, representedCodes) {
  const seen = new Set();
  return (modules || []).filter(m => {
    if (!m || !m.code || seen.has(m.code) || representedCodes.has(m.code)) return false;
    if (m.route || _RETIRED_FLAGS.some(k => m[k]) || !(m.actions || []).length) return false;
    seen.add(m.code);
    return true;
  });
}

function attachAdditionalPermissions(sections, modules) {
  const represented = new Set(sections.flatMap(s => s.subgroups.flatMap(sg => sg.functions.map(f => f.code))).filter(Boolean));
  additionalModules(modules, represented).forEach(m => {
    let sec = sections.find(s => s.label && s.label === m.group);
    if (!sec) {
      sec = sections.find(s => s.key === "additional-permissions");
      if (!sec) { sec = { key: "additional-permissions", label: "Additional Permissions", subgroups: [] }; sections.push(sec); }
    }
    const sgKey = `${sec.key}-additional`;
    let sg = sec.subgroups.find(x => x.key === sgKey);
    if (!sg) { sg = { key: sgKey, label: "Additional Permissions", functions: [] }; sec.subgroups.push(sg); }
    sg.functions.push({
      id: `additional-${m.code}`, label: m.name || m.code, route: "", adminOnly: false, code: m.code,
      actions: [...m.actions], unmapped: false, shared: false, intendedCode: null, legacy: false,
      canonical: !!m.canonical, structuralCleanup: false, additional: true,
    });
  });
}

// Flatten every function in a section (across its subgroups).
export function sectionFunctions(section) {
  return section.subgroups.flatMap(sg => sg.functions);
}

// Unique MAPPED functions of a branch, de-duplicated by canonical code — so
// two sidebar nodes sharing one canonical identity are counted once. Actions
// are UNIONed across the sharing nodes so branch/column bulk and tri-state
// cover every applicable action of the shared module.
export function mappedByCode(functions) {
  const map = new Map();
  functions.forEach(f => {
    if (!f.code || f.unmapped) return;
    if (!map.has(f.code)) map.set(f.code, { ...f, actions: [...f.actions] });
    else { const e = map.get(f.code); f.actions.forEach(a => { if (!e.actions.includes(a)) e.actions.push(a); }); }
  });
  return Array.from(map.values());
}

// ── Grant (local editor) helpers — grants is { [code]: Set<action> } ──────
export function cloneGrants(g) { const o = {}; for (const k in g) o[k] = new Set(g[k]); return o; }

export function grantsFromPermissions(perms) {
  const g = {};
  (perms || []).forEach(p => { (g[p.module_code] ||= new Set()).add(p.action); });
  return g;
}

export function countActions(grants, code, actions) {
  const set = grants[code]; if (!set) return 0;
  return actions.reduce((n, a) => n + (set.has(a) ? 1 : 0), 0);
}

// RBAC-DEPENDENCY-01 · independent selection: a toggle changes ONLY the selected action.
export function applyToggle(g, code, action, on) {
  const set = new Set(g[code] || []);
  if (on) set.add(action); else set.delete(action);
  g[code] = set;
}

// RBAC-DEPENDENCY-01 · mapped functions holding any action but lacking View (advisory only).
export function lacksView(set, actions) {
  return !!set && set.size > 0 && actions.includes("view") && !set.has("view");
}
export function missingViewFunctions(g, funcs) {
  return funcs.filter(f => lacksView(g[f.code], f.actions)).map(f => f.code).sort();
}

// Branch bulk primitives (operate on the de-duplicated mapped functions).
export function bulkSelectAll(g, funcs) { funcs.forEach(f => { g[f.code] = new Set(f.actions); }); }
// RBAC-DEPENDENCY-01 Q2=B · View All ADDS View; every other selected action is kept.
export function bulkViewOnly(g, funcs) {
  funcs.forEach(f => { if (f.actions.includes("view")) g[f.code] = new Set([...(g[f.code] || []), "view"]); });
}
export function bulkClear(g, funcs) { funcs.forEach(f => { g[f.code] = new Set(); }); }
export function bulkColumn(g, funcs, action, on) {
  funcs.forEach(f => { if (f.actions.includes(action)) applyToggle(g, f.code, action, on); });
}

// Tri-state aggregate over a set of (de-duplicated) mapped functions.
// Returns "none" | "all" | "partial" | "empty" (no selectable cells).
export function triState(g, funcs) {
  let total = 0, sel = 0;
  funcs.forEach(f => { total += f.actions.length; sel += countActions(g, f.code, f.actions); });
  if (total === 0) return "empty";
  if (sel === 0) return "none";
  if (sel === total) return "all";
  return "partial";
}

// Diff two grant maps → { added:[{code,action}], removed:[{code,action}] }.
export function diffGrants(original, editor) {
  const flat = (g) => { const s = new Set(); for (const c in g) g[c].forEach(a => s.add(`${c}:${a}`)); return s; };
  const o = flat(original), e = flat(editor);
  const added = [], removed = [];
  e.forEach(k => { if (!o.has(k)) { const [code, action] = k.split(":"); added.push({ code, action }); } });
  o.forEach(k => { if (!e.has(k)) { const [code, action] = k.split(":"); removed.push({ code, action }); } });
  const sort = (x) => x.sort((a, b) => (a.code + a.action).localeCompare(b.code + b.action));
  return { added: sort(added), removed: sort(removed) };
}

// Index canonical code → { branch, subgroup, label } for labelling diffs and
// for detecting grants that are NOT represented anywhere in the sidebar tree
// (legacy / orphan modules → surfaced in the diagnostic panel, never the
// normal matrix).
export function codeIndex(tree) {
  const idx = {};
  tree.forEach(sec => sec.subgroups.forEach(sg => sg.functions.forEach(f => {
    if (f.code && !idx[f.code]) idx[f.code] = { branch: sec.label, subgroup: sg.label, label: f.label };
  })));
  return idx;
}


// Flatten the FULL editor grant map → [{module_code, action}] for the Save
// payload. Includes EVERY code currently in the editor — canonical functions
// AND legacy/orphan codes the matrix never renders — so an authoritative
// desired-state PUT preserves legacy grants (Stage 2C §16) rather than wiping
// them. Bulk controls only ever touch mapped canonical codes, so orphan codes
// ride through untouched.
export function permissionsPayload(grants) {
  const out = [];
  for (const code in grants) {
    (grants[code] || new Set()).forEach(a => out.push({ module_code: code, action: a }));
  }
  return out;
}

// A1-D3 · authoritative registry (GET /rbac/modules) → { code: Set<action> }.
export function registryActions(modules) {
  const r = {};
  (modules || []).forEach(m => { if (m && m.code) r[m.code] = new Set(m.actions || []); });
  return r;
}

// A1-D3 · stored grants whose module or action is absent from the registry (mirrors the A1-D2 backend rule).
export function invalidGrants(grants, registry) {
  const out = [];
  for (const code in grants) {
    (grants[code] || new Set()).forEach(a => {
      const reason = NON_ASSIGNABLE_ALIASES.has(code) ? "non_assignable_alias"
        : !registry[code] ? "unknown_module" : !registry[code].has(a) ? "unsupported_action" : null;
      if (reason) out.push({ code, action: a, reason, key: `${code}:${a}` });
    });
  }
  return out.sort((x, y) => x.key.localeCompare(y.key));
}

export function sameGrants(a, b) {
  const d = diffGrants(a, b);
  return d.added.length === 0 && d.removed.length === 0;
}
